/// <reference types="@types/googlemaps" />
/*global google*/

import _ from 'lodash';
import { calculateColor } from './colorHelper';
import { formatValue } from 'utilities/format';
import defaultPropertyIcon from 'assets/images/rex/default-property.png';
import satellitePropertyIcon from 'assets/images/rex/satellite-property.png';
import selectedPropertyIcon from 'assets/images/rex/selected-property.png';
import defaultAggregateIcon from 'assets/images/rex/default-aggregate.png';
import satelliteAggregateIcon from 'assets/images/rex/satellite-aggregate.png';
import selectedAggregateIcon from 'assets/images/rex/selected-aggregate.png';

const MAX_DELTA = 0.0001;
const DEFAULT_FILL = '#308085'; //rgb(75, 156, 161)
const SATELLITE_DEFAULT_FILL = '#ffff00'; //rgb(255, 255, 0)
const SELECTED_FILL = '#e58c00'; //rgb(229, 140, 0)
const DEFAULT_NO_DATA = '#afafaf'; //rgb(175, 175, 175)
const SATELLITE_DEFAULT_NO_DATA = '#ffffff'; //rgb(255, 255, 255)

export function isSatelliteMode(mapType) {
  if (
    mapType === window.google.maps.MapTypeId.SATELLITE ||
    mapType === window.google.maps.MapTypeId.HYBRID ||
    mapType.includes('satellite')
  ) {
    return true;
  }
  return false;
}

export function hasBoundsChanged(newLocation, oldBounds, maxDelta = MAX_DELTA) {
  if (newLocation == null || oldBounds == null || newLocation.bounds == null)
    return false;

  const exceedsDelta = (bound1, bound2) => {
    return Math.abs(bound1 - bound2) > maxDelta;
  };

  if (
    exceedsDelta(newLocation.bounds[0], oldBounds[0]) ||
    exceedsDelta(newLocation.bounds[1], oldBounds[1]) ||
    exceedsDelta(newLocation.bounds[2], oldBounds[2]) ||
    exceedsDelta(newLocation.bounds[3], oldBounds[3])
  ) {
    return true;
  }

  return false;
}

export function hasLocationChanged(
  newLocation,
  oldLat,
  oldLng,
  oldZoom,
  maxDelta = MAX_DELTA
) {
  if (newLocation == null) return false;
  if (newLocation.zoom !== oldZoom) return true;

  const latDelta = Math.abs(newLocation.center.lat - oldLat);
  const lngDelta = Math.abs(newLocation.center.lng - oldLng);

  if (latDelta > maxDelta) return true;
  if (lngDelta > maxDelta) return true;

  return false;
}

export function getLocationFromMap(map) {
  if (!map) return null;
  const bounds = [];
  const mapBounds = map.getBounds();
  if (mapBounds) {
    //! order matters for back-end api !
    bounds.push(mapBounds.getSouthWest().lat());
    bounds.push(mapBounds.getNorthEast().lat());
    bounds.push(mapBounds.getSouthWest().lng());
    bounds.push(mapBounds.getNorthEast().lng());
  }
  const center = map.getCenter()?.toJSON();
  const zoom = map.getZoom();
  return { bounds, center, zoom };
}

//marker playground: http://jsfiddle.net/v1yc13uo/

export function getIconColor(selected, mapType) {
  if (selected) {
    return SELECTED_FILL;
  } else if (
    mapType === window.google.maps.MapTypeId.SATELLITE ||
    mapType === window.google.maps.MapTypeId.HYBRID ||
    mapType.includes('satellite')
  ) {
    return SATELLITE_DEFAULT_FILL;
  }
  return DEFAULT_FILL;
}

export function getIconBorderColor(
  selected,
  hasPinDisplayType,
  valueColor,
  mapType
) {
  if (
    mapType === window.google.maps.MapTypeId.SATELLITE ||
    mapType === window.google.maps.MapTypeId.HYBRID ||
    mapType.includes('satellite')
  ) {
    return SATELLITE_DEFAULT_FILL;
  } else if (selected) {
    return SELECTED_FILL;
  } else if (hasPinDisplayType) {
    return valueColor;
  }
  return DEFAULT_FILL;
}

export function createAggregateIcon(
  selected = false,
  mapType = window.google.maps.MapTypeId.ROADMAP,
  dataField,
  colorMatrix
) {
  if (dataField !== 'propertiesCount' && !selected) {
    return {
      path: 'M46.5,16.5c0,8.84-10.3,16-23,16s-23-7.16-23-16S10.8.5,23.5.5,46.5,7.66,46.5,16.5Z',
      fillColor: colorMatrix.valueColor,
      fillOpacity: selected ? 1 : 0.9,
      scale: selected ? 1 : 0.87,
      strokeColor: colorMatrix.valueColor,
      strokeWeight: 10,
      labelOrigin: p(24, 17),
      anchor: p(24, 38),
      strokeOpacity: 0.5,
    };
  } else {
    if (selected) return selectedAggregateIcon;
    if (
      mapType === window.google.maps.MapTypeId.SATELLITE ||
      mapType === window.google.maps.MapTypeId.HYBRID ||
      mapType.includes('satellite')
    )
      return satelliteAggregateIcon;
    return defaultAggregateIcon;
  }
}

export function createPropertyIcon(
  selected = false,
  mapType = window.google.maps.MapTypeId.ROADMAP
) {
  if (selected) return selectedPropertyIcon;
  if (
    mapType === window.google.maps.MapTypeId.SATELLITE ||
    mapType === window.google.maps.MapTypeId.HYBRID ||
    mapType.includes('satellite')
  )
    return satellitePropertyIcon;
  return defaultPropertyIcon;
}

export function createPropertyWithInfoIcon(
  selected = false,
  fill = DEFAULT_FILL
) {
  return {
    background: fill,
    color: `white`,
    padding: '4px 6px 4px 6px',
    borderRadius: `4px`,
    opacity: selected ? 1 : 0.9,
  };
}

export function createMapPinIcon() {
  return {
    path: 'M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z',
    scale: 1.5,
    strokeWeight: 1,
    strokeColor: '#ffffff',
    fillColor: '#ce2d78',
    fillOpacity: 1,
    anchor: p(10, 10),
  };
}

export function getLabelColor(mapType, selected) {
  if (
    (mapType === window.google.maps.MapTypeId.SATELLITE ||
      mapType === window.google.maps.MapTypeId.HYBRID ||
      mapType.includes('satellite')) &&
    !selected
  ) {
    return 'black';
  }
  return 'white';
}

export function createLabel(
  pin,
  pinDisplayType,
  propertiesCountFieldObj,
  color = 'white',
  isLoading = false
) {
  let nullText = isLoading ? 'Loading...' : '-n/a-';
  const text =
    pinDisplayType && pin?.data?.[pinDisplayType.dataField] != null
      ? formatValue(
          pin.data[pinDisplayType.dataField],
          pinDisplayType.format,
          true,
          nullText
        )
      : nullText;

  let t = `${text}`;
  const propCountFormatted = propertiesCountFieldObj
    ? formatValue(
        pin.data.propertiesCount,
        propertiesCountFieldObj.format,
        true,
        '-n/a-'
      )
    : null;
  return {
    propCountFormatted,
    text: t,
    color: color,
    fontWeight: 'bold',
    fontSize: '13px',
  };
}

export function createPosition(lat, lng) {
  return new google.maps.LatLng(lat, lng);
}

export function createPolygonCoordinates(shape) {
  if (shape && shape.polygons && shape.polygons.length > 0) {
    var coordsArray = [];
    for (let p = 0; p < shape.polygons.length; p++) {
      const polygon = shape.polygons[p];
      var coords = [];
      for (let c = 0; c < polygon.Coordinates.length; c++) {
        const coordinate = polygon.Coordinates[c];
        coords.push({ lat: coordinate.Latitude, lng: coordinate.Longitude });
      }
      coordsArray.push(coords);
    }
    return coordsArray;
  } else {
    return null;
  }
}

export function createCustomShapeCoordinates(shape) {
  if (shape && shape.polygons && shape.polygons.length > 0) {
    var coordsArray = [];
    for (let p = 0; p < shape.polygons.length; p++) {
      const polygon = shape.polygons[p];

      coordsArray.push(mapCoordinates(polygon.Shell));

      // hole coordinate arrays are already reversed on server
      polygon.Holes.map((hole) => {
        coordsArray.push(mapCoordinates(hole));
      });
    }

    return coordsArray;
  } else {
    return null;
  }
}

export function mapCoordinates(coordinates) {
  return coordinates.map((c) => {
    return { lat: c.Latitude, lng: c.Longitude };
  });
}

export function getShapeById(shapesData, shapeId) {
  if (shapesData && shapesData.length > 0 && shapeId) {
    const q = shapesData.filter((x) => x.Geo === shapeId);
    return q != null && q.length === 1 ? q[0] : null;
  } else {
    return null;
  }
}

export function getNoDataColor(mapType) {
  if (
    mapType === window.google.maps.MapTypeId.SATELLITE ||
    mapType === window.google.maps.MapTypeId.HYBRID ||
    mapType.includes('satellite')
  ) {
    return SATELLITE_DEFAULT_NO_DATA;
  }
  return DEFAULT_NO_DATA;
}

export function getColorForShape(
  shapeColorPalette,
  shape,
  shapesData,
  colorByField,
  colorByDataType
) {
  const colorMatrix = {};
  if (
    shape &&
    shapesData &&
    shapesData.length > 0 &&
    colorByField &&
    colorByDataType
  ) {
    if (colorByDataType === 'number') {
      colorMatrix.value = shape[colorByField];
      const maxObj = _.maxBy(shapesData, colorByField);
      const minObj = _.minBy(shapesData, colorByField);
      if (minObj && maxObj) {
        colorMatrix.maxValue = maxObj[colorByField];
        colorMatrix.minValue = minObj[colorByField];
        colorMatrix.midValue =
          colorMatrix.maxValue -
          (colorMatrix.maxValue - colorMatrix.minValue) / 2;
      }
      if (
        colorMatrix.value != null &&
        colorMatrix.minValue != null &&
        colorMatrix.midValue != null &&
        colorMatrix.maxValue != null
      ) {
        colorMatrix.valueColor = calculateColor(
          shapeColorPalette,
          colorMatrix.minValue,
          colorMatrix.maxValue,
          colorMatrix.midValue,
          colorMatrix.value
        );
        colorMatrix.minValueColor = calculateColor(
          shapeColorPalette,
          colorMatrix.minValue,
          colorMatrix.maxValue,
          colorMatrix.midValue,
          colorMatrix.minValue
        );
        colorMatrix.midValueColor = calculateColor(
          shapeColorPalette,
          colorMatrix.minValue,
          colorMatrix.maxValue,
          colorMatrix.midValue,
          colorMatrix.midValue
        );
        colorMatrix.maxValueColor = calculateColor(
          shapeColorPalette,
          colorMatrix.minValue,
          colorMatrix.maxValue,
          colorMatrix.midValue,
          colorMatrix.maxValue
        );
      }
    } else {
      const hasValue = !!shape[colorByField];
      colorMatrix.valueColor = hasValue
        ? calculateColor(shapeColorPalette, 1, 1, 1, 1)
        : DEFAULT_NO_DATA;
    }
  }
  if (
    !colorMatrix.valueColor ||
    colorMatrix.valueColor === 'rgb(NaN, NaN, NaN)' ||
    colorMatrix.valueColor.indexOf('NaN') >= 0
  ) {
    colorMatrix.valueColor = DEFAULT_NO_DATA;
  }
  return colorMatrix;
}

export function getColorForPin(
  pinColorPalette,
  value,
  maxObj,
  minObj,
  colorByField,
  noDataColor = DEFAULT_NO_DATA
) {
  const colorMatrix = {};
  if (value && maxObj && minObj && colorByField) {
    colorMatrix.value = value;
    if (minObj && maxObj) {
      colorMatrix.maxValue = maxObj[colorByField];
      if (value > colorMatrix.maxValue) {
        colorMatrix.value = colorMatrix.maxValue;
      }
      colorMatrix.minValue = minObj[colorByField];
      if (value < colorMatrix.minValue) {
        colorMatrix.value = colorMatrix.minValue;
      }
      colorMatrix.midValue =
        colorMatrix.maxValue -
        (colorMatrix.maxValue - colorMatrix.minValue) / 2;
    }
    if (
      colorMatrix.value != null &&
      colorMatrix.minValue != null &&
      colorMatrix.midValue != null &&
      colorMatrix.maxValue != null
    ) {
      colorMatrix.valueColor = calculateColor(
        pinColorPalette,
        colorMatrix.minValue,
        colorMatrix.maxValue,
        colorMatrix.midValue,
        colorMatrix.value
      );
      colorMatrix.minValueColor = calculateColor(
        pinColorPalette,
        colorMatrix.minValue,
        colorMatrix.maxValue,
        colorMatrix.midValue,
        colorMatrix.minValue
      );
      colorMatrix.midValueColor = calculateColor(
        pinColorPalette,
        colorMatrix.minValue,
        colorMatrix.maxValue,
        colorMatrix.midValue,
        colorMatrix.midValue
      );
      colorMatrix.maxValueColor = calculateColor(
        pinColorPalette,
        colorMatrix.minValue,
        colorMatrix.maxValue,
        colorMatrix.midValue,
        colorMatrix.maxValue
      );
    }
  }
  if (
    !colorMatrix.valueColor ||
    colorMatrix.valueColor === 'rgb(NaN, NaN, NaN)' ||
    colorMatrix.valueColor.indexOf('NaN') >= 0
  ) {
    colorMatrix.valueColor = noDataColor;
  }
  return colorMatrix;
}

function p(x, y) {
  return new google.maps.Point(x, y);
}
